import { useForm } from "react-hook-form";
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";

import { useToast } from "hooks/useToast";
import useErrorHandler from "hooks/useErrorHandler";
import { useGetSingleSpeciesQuery, useUpdateSingleSpeciesMutation } from "api";
import {
  Modal,
  Divider,
  Input,
  Label,
  ReadOnly,
  Tabs,
  FormGroup,
  Button,
} from "@mwi/ui";

type Inputs = {
  translation: string | undefined;
};

type EditVocabularyModalTabs = "translation";

type EditSpeciesVocabularyModalProps = {
  isOpen: boolean;
  onClose: () => void;
};

const EditSpeciesVocabularyModal = ({
  isOpen,
  onClose,
}: EditSpeciesVocabularyModalProps): JSX.Element => {
  const [selectedTab, setSelectedTab] =
    useState<EditVocabularyModalTabs>("translation");

  const { errorToast, successToast } = useToast();
  const errorHandler = useErrorHandler();

  const { id } = useParams();

  const {
    data: singleSpecies,
    isFetching: fetchingSingleSpecies,
    isError: errorFetchingSingleSpecies,
  } = useGetSingleSpeciesQuery(
    {
      speciesUuid: id!,
    },
    { skip: !id },
  );

  const [
    updateSingleSpecies,
    {
      error: singleSpeciesErrors,
      isLoading: updatingSingleSpecies,
      isSuccess: didUpdateSingleSpecies,
      isError: errorUpdatingSingleSpecies,
    },
  ] = useUpdateSingleSpeciesMutation();

  const {
    register,
    handleSubmit,
    setValue,
    reset,
    setError,
    formState: { errors },
  } = useForm<Inputs>();

  useEffect(() => {
    if (singleSpecies) {
      setValue("translation", singleSpecies?.translation ?? undefined);
    }
  }, [singleSpecies, id]);

  useEffect(() => {
    if (didUpdateSingleSpecies) {
      onClose();
      successToast({
        title: "Species Updated",
        message: `${singleSpecies?.name} has been updated`,
      });
    }
  }, [didUpdateSingleSpecies]);

  useEffect(() => {
    if (errorUpdatingSingleSpecies) {
      errorHandler(singleSpeciesErrors, setError);
    }

    if (errorFetchingSingleSpecies) {
      onClose();
      errorToast({
        title: "Unable to edit species",
        message:
          "We're unable to update this species at the moment. Please try again later.",
      });
    }
  }, [errorUpdatingSingleSpecies, errorFetchingSingleSpecies]);

  const handleSave = (data: Inputs) => {
    if (singleSpecies) {
      updateSingleSpecies({
        speciesUuid: singleSpecies.id,
        body: {
          enabled: singleSpecies.enabled,
          translation: data.translation,
        },
      });
    }
  };

  const handleAfterLeave = () => {
    reset();
    setSelectedTab("translation");
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      loading={fetchingSingleSpecies}
      className="flex flex-col tablet:!max-w-[776px]"
      afterLeave={handleAfterLeave}
    >
      <Modal.Header title="Edit Species" />

      <Modal.Content className="flex flex-col flex-grow">
        {singleSpecies && (
          <>
            <Tabs className="gap-x-10">
              <Tabs.Item
                title="Translation"
                isActive={selectedTab === "translation"}
                onClick={() => setSelectedTab("translation")}
              />
            </Tabs>

            <form
              className="flex flex-col justify-between flex-grow mt-12"
              onSubmit={handleSubmit(handleSave)}
            >
              {selectedTab === "translation" && (
                <div className="mb-[228px]">
                  <ReadOnly
                    heading="Species name"
                    value={singleSpecies.name}
                    className="!mb-3"
                  />

                  <Divider className="mb-9" />

                  <FormGroup error={errors.translation?.message}>
                    <Label className="!text-sm !leading-[21px]">
                      Translation
                    </Label>
                    <Input size="small" {...register("translation")} />
                  </FormGroup>
                </div>
              )}

              <Button
                type="submit"
                className="max-w-[153px] mt-4"
                loading={updatingSingleSpecies}
              >
                Save
              </Button>
            </form>
          </>
        )}
      </Modal.Content>
    </Modal>
  );
};

export default EditSpeciesVocabularyModal;
